import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../constants';
import { getLanguage, setLanguage, LANGUAGES } from '../i18n';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // Impersonation: when set, we are operating as another user.
  // `originalToken` is the staff member's real token we restore on exit.
  const [impersonation, setImpersonation] = useState(() => {
    try {
      const raw = localStorage.getItem('impersonation');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // ------------------------------------------------------------------
  // Single source of truth for the Authorization header.
  //
  // Jun 2026 incident: the owner's super-admin session got
  // "Admin or staff access required" on the Rush Fee override (and other
  // admin endpoints) while the admin desk itself kept working. Cause: the
  // app reads auth from TWO places — `axios.defaults` set at login, and a
  // fresh `localStorage.getItem('token')` inside individual handlers.
  // Signing into another account anywhere on this origin (second tab, the
  // client portal) overwrites localStorage, so only the handlers that
  // re-read it start sending the other account's token — producing 403s
  // that look random. These interceptors make every request use the
  // CURRENT stored token, and a cross-tab change immediately re-resolves
  // who we are instead of leaving a stale admin shell on screen.
  // ------------------------------------------------------------------
  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      const stored = localStorage.getItem('token');
      if (stored) config.headers.Authorization = `Bearer ${stored}`;
      return config;
    });

    const resId = axios.interceptors.response.use(
      (r) => r,
      async (error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        const url = error?.config?.url || '';
        const accessDenied =
          status === 403 &&
          typeof detail === 'string' &&
          /access required|not authorized|permission required/i.test(detail);
        if (accessDenied && !url.includes('/auth/me')) {
          try {
            const me = await axios.get(`${API}/auth/me`);
            const role = me.data?.role;
            if (!['super_admin', 'admin', 'staff'].includes(role)) {
              error.response.data.detail =
                `${detail} — this browser is signed in as ${me.data?.email} (${role}). ` +
                `Sign in with your admin account to continue.`;
              toast.error(
                `Signed in as ${me.data?.email} (${role}) — admin actions are blocked. Sign in again as an admin.`,
                {
                  duration: 12000,
                  action: { label: 'Sign in again', onClick: () => logout() },
                },
              );
            }
          } catch { /* identity lookup failed — leave the original error */ }
        }
        return Promise.reject(error);
      },
    );

    const onStorage = (e) => {
      if (e.key !== 'token' || e.newValue === token) return;
      setToken(e.newValue);
      if (e.newValue) {
        toast.warning('Your sign-in changed in another tab — reloading this screen for that account.',
          { duration: 8000 });
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
      window.removeEventListener('storage', onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUser = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setLoading(false);
      // Follow the account's saved language (non-admin users only; admin UI stays English)
      const pref = response.data?.settings?.preferred_language;
      const isStaff = ['super_admin', 'admin', 'staff'].includes(response.data?.role);
      if (!isStaff && pref && pref !== getLanguage() && LANGUAGES.some((l) => l.code === pref)) {
        setLanguage(pref);
      }
    } catch (error) {
      const status = error?.response?.status;
      // Only force-logout on a REAL 401/403 — an actual auth failure.
      // Network errors, 5xx, timeouts (backend restarting, pod cold-start,
      // Cloudflare origin blip) should NOT wipe the session — they should
      // retry with backoff. This prevents the "silently kicked out during
      // a deploy" symptom the OOM incident exposed on Feb 27, 2026.
      if (status === 401 || status === 403) {
        localStorage.removeItem('token');
        setToken(null);
        delete axios.defaults.headers.common['Authorization'];
        setLoading(false);
        return;
      }
      // Transient failure — retry up to 3 times with backoff (1s, 3s, 6s).
      if (retryCount < 3) {
        const delay = [1000, 3000, 6000][retryCount] || 6000;
        setTimeout(() => fetchUser(retryCount + 1), delay);
        return; // keep loading=true so UI doesn't flash logged-out
      }
      // After 3 retries still failing — surface as "loaded but no user".
      // Token stays in localStorage so a page refresh once the backend is
      // healthy will restore the session automatically.
      console.warn('[auth] /me still failing after retries; keeping token for next reload', error);
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    // A fresh sign-in ends any impersonation left behind by a previous session. Without this, the
    // stale record survives in localStorage: its expiry timer can later call endImpersonation and
    // swap this new session for an old token, dropping a signed-in admin into a client view.
    localStorage.removeItem('impersonation');
    setImpersonation(null);
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('impersonation');
    setToken(null);
    setUser(null);
    setImpersonation(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  // Begin acting as another user. `info` is the JSON from POST /impersonate.
  const startImpersonation = (info) => {
    const originalToken = localStorage.getItem('token');
    const payload = { ...info, original_token: originalToken };
    localStorage.setItem('impersonation', JSON.stringify(payload));
    localStorage.setItem('token', info.token);
    setImpersonation(payload);
    setToken(info.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${info.token}`;
  };

  // Restore the original staff token.
  const endImpersonation = () => {
    const orig = impersonation?.original_token;
    localStorage.removeItem('impersonation');
    if (orig) {
      localStorage.setItem('token', orig);
      setImpersonation(null);
      setToken(orig);
      axios.defaults.headers.common['Authorization'] = `Bearer ${orig}`;
    } else {
      // Fallback: log out entirely
      logout();
    }
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, logout, fetchUser,
      impersonation, startImpersonation, endImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
